import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { DialogBase } from 'src/Libs/flow/components'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { useFetchInstallation } from 'src/Libs/flow/installation/hooks'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { SaveStatusRow, saveStatus, SaveStatus } from 'src/Libs/flow/advanced-settings/components/Dialogs'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

export interface InstallationPairingDialogProps {
  installationId: number
  message: string
  request: () => Promise<unknown>
  onOperationFinished: () => void
  onClose?: () => void
}

export const InstallationPairingDialog: React.FC<InstallationPairingDialogProps> = ({
  installationId,
  message,
  request,
  onOperationFinished,
  onClose,
}) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  /**
   * Counter for the number of operations that have not finished yet.
   * Goes down by one each time an operation finishes.
   */
  const [operationPending, setOperationPending] = useState(true)
  const countOperationFinished = (): void => setOperationPending(false)
  const operationFinished = !operationPending

  const [syncFinished, setSyncFinished] = useState(false)

  const queryClient = useQueryClient()

  const hasFired = useRef(false)
  useEffect(() => {
    if (operationFinished && syncFinished && !hasFired.current) {
      hasFired.current = true
      onOperationFinished()
    }
  }, [queryClient, operationFinished, syncFinished, installationId, onOperationFinished])

  const { installation: newInstallationValues } = useFetchInstallation(installationId, operationFinished)

  const isInstallationPending = !!useIsFetching({ queryKey: ['installation', installationId] })

  const canClose = operationFinished && syncFinished && !isInstallationPending && !!newInstallationValues

  const handleCloseDialog = (): void => {
    if (!canClose) return

    onClose?.()
  }

  return (
    <DialogBase closeDialog={handleCloseDialog} disableCloseButton={!canClose} fullWidth maxWidth={'md'} open>
      <DialogTitle variant="h2">{t('installation.save.title')}</DialogTitle>

      <DialogContent>
        <Row name={message} request={request} onSettled={countOperationFinished} />

        <Row
          name={t('installation.save.synchronization')}
          request={() => syncInstallation(installationId, authFetch)}
          onSettled={() => setSyncFinished(true)}
          loadingLabel={t('installation.sync.status.inProgress')}
          completeLabel={t('installation.sync.status.done')}
          disabled={!operationFinished}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          size="large"
          color="secondary"
          disabled={!operationFinished || !syncFinished || isInstallationPending}
          onClick={onClose}
        >
          {t('common.ok')}
        </Button>
      </DialogActions>
    </DialogBase>
  )
}

type RowProps<T> = {
  name: string
  request: () => Promise<T>
  onSuccess?: () => void
  onSettled: () => void
  disabled?: boolean
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel?: string
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel?: string
}

/* eslint-disable-next-line  */
const Row: React.FC<RowProps<any>> = <T,>({ name, request, onSuccess, onSettled, disabled, loadingLabel, completeLabel }: RowProps<T>) => {
  const [status, setStatus] = useState<SaveStatus>(saveStatus.idle)

  const { mutate, error, isIdle } = usePortalMutation({
    mutationFn: async () => new ApiResult(await request()),
    onMutate: () => {
      setStatus(saveStatus.loading)
    },
    onError: () => {
      setStatus(saveStatus.error)
    },
    onSuccess: _data => {
      onSuccess?.()
      setStatus(saveStatus.complete)
    },
    onSettled,
  })

  // Workaround to send the request once, even during development.
  const hasFired = useRef(false)
  useEffect(() => {
    if (!disabled && isIdle && !hasFired.current) {
      hasFired.current = true
      mutate()
    }
  }, [disabled, isIdle, mutate])

  return (
    <SaveStatusRow
      name={name}
      status={status}
      errorMessage={error?.message}
      disabled={disabled ?? false}
      loadingLabel={loadingLabel}
      completeLabel={completeLabel}
    />
  )
}
